import React, { useRef, useEffect, useCallback, useState } from "react";
import { Box, Typography, CircularProgress, Alert, Button, IconButton, Tooltip } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { PostCard } from "./PostCard";

const font = "Host Grotesk, sans-serif";

function StreamBanner({ connected, newLiveCount, tab }) {
  return (
    <Box sx={{
      mx: 2.5, mt: 1.5, px: 2, py: 1.25, borderRadius: "10px",
      display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0,
      bgcolor: connected ? "rgba(139,92,246,0.05)" : "rgba(245,158,11,0.05)",
      border: `1px solid ${connected ? "rgba(139,92,246,0.15)" : "rgba(245,158,11,0.18)"}`,
    }}>
      {connected ? (
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: "#8B5CF6", animation: "glow 1.5s ease-in-out infinite" }} />
      ) : (
        <CircularProgress size={14} thickness={5} sx={{ color: "#F59E0B", flexShrink: 0 }} />
      )}
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: connected ? "#5B21B6" : "#D97706", fontFamily: font, mb: 0.25 }}>
          {connected ? `Watching for new ${tab} data` : "Reconnecting to stream…"}
        </Typography>
        <Typography sx={{ fontSize: "0.66rem", color: connected ? "#7C3AED" : "#F59E0B", opacity: 0.75, fontFamily: font }}>
          {connected ? "New entries appear instantly as they arrive" : "Showing existing results"}
        </Typography>
      </Box>
      {newLiveCount > 0 && connected && (
        <Typography sx={{ fontSize: "0.69rem", fontFamily: "monospace", fontWeight: 800, color: "#5B21B6", bgcolor: "rgba(139,92,246,0.12)", px: 1.25, py: 0.375, borderRadius: "8px", whiteSpace: "nowrap" }}>
          +{newLiveCount} live
        </Typography>
      )}
    </Box>
  );
}

function LoadingState() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
      <CircularProgress sx={{ color: "#0f766e" }} />
    </Box>
  );
}

function ErrorState({ tab, error, onRetry }) {
  return (
    <Alert
      severity="error"
      sx={{ mx: 2.5, mt: 2, fontFamily: font, backgroundColor: "#fef2f2", borderRadius: "12px", border: "1px solid #fecaca" }}
      action={
        <Button size="small" onClick={onRetry} sx={{ fontFamily: font, color: "#dc2626", textTransform: "none" }}>
          Retry
        </Button>
      }
    >
      {error || `No posts found for this session`}
    </Alert>
  );
}

function EmptyState({ tab, isStreaming }) {
  return (
    <Box sx={{ textAlign: "center", p: 4 }}>
      <Typography variant="h6" sx={{ color: "#64748b", mb: 1, fontFamily: font }}>
        No {tab} data yet
      </Typography>
      <Typography variant="body2" sx={{ color: "#94a3b8", fontFamily: font }}>
        {isStreaming ? "Waiting for the first entry…" : "No records found for this session"}
      </Typography>
    </Box>
  );
}

export function DataBody({
  tab, session, sessionType,
  posts, loading, loadingMore, error, onRetry,
  useStream, streamConnected, newLiveCount, streamedIds,
  totalCount, hasMore, onLoadMore,
}) {
  const scrollRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Keep latest values in refs so handleScroll is always stable (never recreated).
  // This avoids the listener being removed/re-added on every state change, and
  // ensures the scroll container ref stays valid even when posts reset between fetches.
  const loadingMoreRef = useRef(loadingMore);
  const hasMoreRef     = useRef(hasMore);
  const onLoadMoreRef  = useRef(onLoadMore);
  loadingMoreRef.current = loadingMore;
  hasMoreRef.current     = hasMore;
  onLoadMoreRef.current  = onLoadMore;

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowScrollTop(el.scrollTop > 300);
    if (!loadingMoreRef.current && hasMoreRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop <= clientHeight + 150) onLoadMoreRef.current();
    }
  }, []); // stable — reads fresh values via refs

  // Attaches once; scroll container is always mounted so scrollRef stays valid.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const showStreamBanner =
    useStream && (streamConnected || (!streamConnected && posts.length > 0));

  return (
    <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative", bgcolor: "rgba(255,255,255,0.7)", backdropFilter: "blur(5px)" }}>

      {showStreamBanner && (
        <StreamBanner connected={streamConnected} newLiveCount={newLiveCount} tab={tab} />
      )}

      {/* Scroll container is always mounted so scrollRef.current never goes null between fetches */}
      <Box
        ref={scrollRef}
        className="sdd-scroll"
        sx={{ flex: 1, overflowY: "auto", p: 4, pb: 8, display: "flex", flexDirection: "column", gap: 3 }}
      >
        {loading && posts.length === 0 && <LoadingState />}

        {!loading && error && <ErrorState tab={tab} error={error} onRetry={onRetry} />}

        {!loading && !error && posts.length === 0 && <EmptyState tab={tab} isStreaming={useStream} />}

        {posts.map((post, index) => (
          <PostCard
            key={post._id || `post-${index}`}
            post={post}
            type={tab}
            sessionType={sessionType}
            isStreamed={useStream && streamedIds?.has(post._id)}
            platform={session?.platform}
          />
        ))}

        {loadingMore && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 3, gap: 1.5 }}>
            <CircularProgress size={30} sx={{ color: "#0f766e" }} />
            <Typography variant="body2" sx={{ color: "#64748b", fontFamily: font }}>
              Loading more posts...
            </Typography>
          </Box>
        )}

        {!hasMore && !loadingMore && !useStream && posts.length > 0 && (
          <Box sx={{ textAlign: "center", p: 3 }}>
            <Typography variant="body2" sx={{ color: "#94a3b8", fontFamily: font }}>
              You've reached the end of all posts
            </Typography>
          </Box>
        )}
      </Box>

      {showScrollTop && (
        <Tooltip title="Scroll to top" placement="left">
          <IconButton
            onClick={scrollToTop}
            sx={{
              position: "absolute", right: 32, bottom: 24, zIndex: 2,
              width: 42, height: 42,
              bgcolor: "#ffffff", color: "#0f766e",
              border: "1px solid #dbe5e1",
              boxShadow: "0 10px 24px rgba(15,23,42,0.16)",
              "&:hover": { bgcolor: "#ecfdf5", transform: "translateY(-2px)", boxShadow: "0 14px 28px rgba(15,23,42,0.18)" },
              transition: "all 0.18s ease",
            }}
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

    </Box>
  );
}
